import React, {ReactNode, useState, useRef, useImperativeHandle} from 'react'
import {createPortal} from 'react-dom'
import {
    useFloating,
    FloatingArrow,
    arrow,
    useInteractions,
    useHover,
    offset,
    UseFloatingOptions,
    Placement,
    shift,
    flip,
    autoUpdate,
    hide,
    useClick,
    useRole,
    useDismiss,
} from '@floating-ui/react'
import styles from './Tooltip.module.scss'
import clsx from 'clsx'

export interface TooltipHandle {
    forceCloseTooltip: () => void
}

export interface TooltipProps {
    children: ReactNode
    content?: ReactNode | string
    title?: ReactNode | string
    position?: Placement | undefined
    className?: string | undefined
    menuClassName?: string | undefined
    centerText?: boolean | undefined
    delay?: number | undefined
    customMenuComponent?: ReactNode | undefined
    disabled?: boolean | undefined
    clickActionEnabled?: boolean
    hoverActionEnabled?: boolean
    onOpenChange?: (isOpen: boolean) => void
    ref?: React.Ref<TooltipHandle> | undefined
}

export const Tooltip = ({
    children,
    content,
    position,
    className,
    menuClassName,
    title,
    centerText,
    delay = 200,
    customMenuComponent,
    disabled,
    clickActionEnabled = false,
    hoverActionEnabled = true,
    onOpenChange,
    ref,
}: TooltipProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const arrowRef = useRef(null)
    const handleOpenChange = (open: boolean) => {
        if(disabled) return
        setIsOpen(open)
        onOpenChange?.(open)
    }

    const forceCloseTooltip = () => {
        setIsOpen(false)
    }

    useImperativeHandle(ref, () => ({
        forceCloseTooltip: forceCloseTooltip,
    }))

    const option: UseFloatingOptions = {
        open: isOpen,
        onOpenChange: handleOpenChange,
        middleware: [
            offset(10),
            shift(),
            flip({fallbackAxisSideDirection: 'start'}),
            arrow({element: arrowRef}),
            hide(),
        ],
        whileElementsMounted: autoUpdate,
        strategy: 'fixed',
    }

    if(position) option.placement = position

    const {refs, floatingStyles, context} = useFloating(option)
    const hover = useHover(context, {
        delay: {
            open: delay,
            close: 0,
        },
        enabled: hoverActionEnabled,
    })
    const click = useClick(context, {
        enabled: clickActionEnabled,
    })
    const dismiss = useDismiss(context)
    const role = useRole(context, {role: 'tooltip'})
    const {getReferenceProps, getFloatingProps} = useInteractions([hover, click, dismiss, role])

    const renderTooltipMenu = () => {
        const hasCustomMenuComponent = customMenuComponent != null
        if(hasCustomMenuComponent) {
            return <div
                className={clsx(styles.tooltipZIndex, menuClassName)}
                ref={refs.setFloating}
                style={floatingStyles}
                {...getFloatingProps()}>
                {customMenuComponent}
            </div>
        }
        return <div
            className={clsx(styles.tooltipMenu, styles.tooltipZIndex, menuClassName)}
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
        >
            <FloatingArrow className={styles.tooltipArrow} context={context} ref={arrowRef} />
            {title != null && <div className={clsx(styles.tooltipTitle, {[styles.center]: centerText})}>{title}</div>}
            {content != null && <div className={clsx(styles.tooltipContent, {[styles.center]: centerText})}>{content}</div>}
        </div>
    }
    return (
        <>
            <div className={clsx(styles.tooltipTrigger, className)} ref={refs.setReference} {...getReferenceProps()}>
                {children}
            </div>
            {isOpen && createPortal(renderTooltipMenu(), document.body)}
        </>
    )
}
